import { useState } from "react";
import { Menu, Bell, LogOut, ChevronDown } from "lucide-react";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "bizpandit-ui";
import { useLogout } from "@/features/auth/hooks/useLogout";
import type { AuthUser } from "@/features/auth/types";

interface NavbarProps {
  user: AuthUser | null;
  onToggleSidebar: () => void;
}

function getInitials(name?: string | null): string {
  if (!name) return "?";
  
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

export default function Navbar({ user, onToggleSidebar }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const logout = useLogout();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-linegray bg-white px-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="rounded-lg p-2 text-navy2/70 transition-colors hover:bg-surface hover:text-navy2"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-5 w-5" />
          
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          className="relative rounded-lg p-2 text-navy2/70 transition-colors hover:bg-surface hover:text-navy2"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-rose" />
        </button>

         <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-surface"
            >
              <Avatar size="sm">
                <AvatarImage src={user?.avatarUrl ?? undefined} alt={user?.name ?? "Account"} />
                <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium text-navy2 sm:block">
                {user?.name ?? "Account"}
              </span>
              <ChevronDown className="h-4 w-4 text-navy2/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled className="text-xs text-navy2/50">
              {user?.email}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => logout.mutate()} className="gap-2 text-caution">
              <LogOut className="h-4 w-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
